import React, { useState, useEffect } from 'react';
import {
  GlassButton,
  GlassInput,
  GlassSelect,
  GlassTextarea,
  GlassCard,
  SectionReveal,
} from '@/design-system';
import type { Feature, Epic, Program } from '@/types';

interface FeatureFormProps {
  initialData?: Feature | null;
  epics: Epic[];
  programs: Program[];
  onSubmit: (data: Partial<Feature>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FeatureForm: React.FC<FeatureFormProps> = ({
  initialData,
  epics,
  programs,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    estimatedTeamWeeks: 0,
    epicId: '',
    leadProgramId: '',
    targetQuarter: 1
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description || '',
        estimatedTeamWeeks: initialData.estimatedTeamWeeks || 0,
        epicId: initialData.epicId,
        leadProgramId: initialData.leadProgramId || '',
        targetQuarter: initialData.targetQuarter
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Feature name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Feature name must be at least 3 characters';
    } else if (formData.name.length > 200) {
      newErrors.name = 'Feature name must not exceed 200 characters';
    }

    if (!formData.epicId) {
      newErrors.epicId = 'EPIC is required';
    }

    if (formData.estimatedTeamWeeks < 0) {
      newErrors.estimatedTeamWeeks = 'Estimated team weeks cannot be negative';
    } else if (formData.estimatedTeamWeeks > 100) {
      newErrors.estimatedTeamWeeks = 'Estimated team weeks seems too high (max 100)';
    }

    if (formData.targetQuarter < 1 || formData.targetQuarter > 4) {
      newErrors.targetQuarter = 'Target quarter must be between 1 and 4';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must not exceed 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      description: formData.description || undefined,
      leadProgramId: formData.leadProgramId || undefined,
      estimatedTeamWeeks: formData.estimatedTeamWeeks || undefined
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const epicOptions = epics.map(epic => ({
    value: epic.id,
    label: `${epic.name} (${epic.investibleStructure?.name || 'No Structure'})`
  }));

  const programOptions = [
    { value: '', label: 'No lead program' },
    ...programs.filter(p => p.isActive).map(program => ({
      value: program.id,
      label: program.name
    }))
  ];

  const quarterOptions = [
    { value: '1', label: 'Q1' },
    { value: '2', label: 'Q2' },
    { value: '3', label: 'Q3' },
    { value: '4', label: 'Q4' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <SectionReveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <GlassInput
              label="Feature Name"
              value={formData.name}
              onChange={(value) => handleInputChange('name', value)}
              error={errors.name}
              placeholder="e.g., User Registration Flow"
              required
              fullWidth
            />
          </div>

          <GlassSelect
            label="EPIC"
            value={formData.epicId}
            onChange={(value) => handleInputChange('epicId', value)}
            options={epicOptions}
            error={errors.epicId}
            required
            fullWidth
            placeholder="Select EPIC"
            helperText="The EPIC this feature belongs to"
          />

          <GlassSelect
            label="Target Quarter"
            value={formData.targetQuarter.toString()}
            onChange={(value) => handleInputChange('targetQuarter', parseInt(value))}
            options={quarterOptions}
            error={errors.targetQuarter}
            fullWidth
            helperText="When this feature should be delivered"
          />

          <GlassInput
            label="Estimated Team Weeks"
            type="number"
            value={formData.estimatedTeamWeeks.toString()}
            onChange={(value) => handleInputChange('estimatedTeamWeeks', parseFloat(value) || 0)}
            error={errors.estimatedTeamWeeks}
            min={0}
            step={0.5}
            fullWidth
            helperText="Estimated effort required in team weeks"
          />

          <GlassSelect
            label="Lead Program"
            value={formData.leadProgramId}
            onChange={(value) => handleInputChange('leadProgramId', value)}
            options={programOptions}
            fullWidth
            helperText="The program primarily responsible for delivery"
          />

          <div className="md:col-span-2">
            <GlassTextarea
              label="Description"
              value={formData.description}
              onChange={(value) => handleInputChange('description', value)}
              placeholder="Describe the feature's functionality, acceptance criteria, and key requirements..."
              rows={4}
              maxLength={1000}
              showCharCount
              error={errors.description}
              fullWidth
            />
          </div>
        </div>
      </SectionReveal>

      {/* Capacity Calculation Preview */}
      <SectionReveal delay={0.1}>
        {formData.estimatedTeamWeeks > 0 && (
          <GlassCard variant="subtle" padding="sm" className="border-accent-blue/20">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Capacity Impact
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground/60">Estimated Effort:</span>
                <span className="ml-2 text-foreground font-medium">
                  {formData.estimatedTeamWeeks} team weeks
                </span>
              </div>
              <div>
                <span className="text-muted-foreground/60">Estimated Cost:</span>
                <span className="ml-2 text-foreground font-medium">
                  ${(formData.estimatedTeamWeeks * 89 * 40).toLocaleString()}
                  <span className="text-muted-foreground/60 text-xs ml-1">(blended rate)</span>
                </span>
              </div>
            </div>
          </GlassCard>
        )}
      </SectionReveal>

      {/* Readiness Information */}
      <SectionReveal delay={0.15}>
        <GlassCard variant="subtle" padding="sm">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
            Feature Readiness
          </h4>
          <p className="text-sm text-muted-foreground/60">
            After creating this feature, you can track its readiness using our 8-item checklist.
            Features need to be at least 70% ready before they can be scheduled for delivery.
          </p>
        </GlassCard>
      </SectionReveal>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-primary">
        <GlassButton
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </GlassButton>
        <GlassButton
          type="submit"
          variant="primary"
          isLoading={isLoading}
          disabled={isLoading}
        >
          {initialData ? 'Update' : 'Create'} Feature
        </GlassButton>
      </div>
    </form>
  );
};

export default FeatureForm;
